// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import { ProvidedAppContext } from '@/App.interfaces';
import { MutationOptions } from 'apollo-client';
import { addMilliseconds, subMilliseconds } from 'date-fns';
import { DocumentNode } from 'graphql';
import gql from 'graphql-tag';
import {
  get,
  isArray,
  isEmpty,
  isNumber,
  isPlainObject,
  isString,
  keys,
  omit,
  set,
  values,
} from 'lodash';
import pluralize from 'pluralize';
import React, { useContext } from 'react';
import {
  createAuthenticatedApolloClient,
  defaultDataIdFromObject,
} from './apollo-helpers';
import { sendBingEvent } from './bing-ads-helpers';
import { sendCJEOrderEvent } from './cj-affiliates-helpers';
import { loadFacebookPixel } from './facebook-helpers';
import { sendGAEvent } from './stat-helpers';

export function sleep(time) {
  return new Promise((resolve) => setTimeout(resolve, time));
}

export function shouldUseClerk() {
  return true;
}

/**
 * This is to get around some limitations with Clerk's redirect URLs.
 * Since 240tutoring.com is the satellite domain, we need to redirect to 240.org
 * which is the main domain. This function will return the correct redirect URL
 * based on the current hostname.
 * @returns string
 */
export function getRedirectUrl() {
  const { protocol, hostname, pathname, search, hash } = window.location;
  if (hostname === 'study.240tutoring.com') {
    return `${protocol}//jobs.240.org/redirect${pathname}${search}${hash}`;
  }
  return window.location.href;
}

/**
 * Sends conversion stats for a payment.
 * @param {Object} params - The parameters for sending conversion stats.
 * @param {import('../App.interfaces').User} [params.user] - The user who made the payment (optional).
 * @param {import('../App.interfaces').Product} [params.product] - The product being purchased (optional).
 * @param {import('../App.interfaces').Subscription} [params.subscription] - The subscription details (optional).
 * @param {import('./stat-helpers').StatsigClientType} [params.statsigClient] - The Statsig client used for logging events (optional).
 */
export function sendPaymentStat({
  user,
  product,
  subscription,
  statsigClient,
}) {
  if (!subscription || !user || !product) {
    return;
  }

  sendGAEvent('purchase', {
    currency: 'USD',
    transaction_id: subscription.billing,
    value: product.price / 100,
    items: [
      {
        item_id: product.id,
        item_name: product.name,
        item_category: 'Subscription',
        price: product.price / 100,
        quantity: 1,
      },
    ],
  });

  // Send conversion tracking to Statsig
  statsigClient?.logEvent('subscription_purchase_success', 1, {
    product_id: product?.id,
    product_name: product?.name,
    product_price: product.price / 100,
  });

  // Send conversion tracking stats to facebook
  const fbPixelId =
    window.ReactAppEnv &&
    window.ReactAppEnv.stats &&
    window.ReactAppEnv.stats.fbPixelId;
  if (fbPixelId) {
    const fbq = loadFacebookPixel({ pixelId: fbPixelId });
    fbq('track', 'Purchase', {
      content_category: 'Subscription',
      content_ids: [product.id],
      content_name: product.name,
      content_type: 'product',
      currency: 'USD',
      value: product.price / 100.0,
    });
  }

  // Send conversion tracking stats to cj affiliates
  // for the first subscription a user purchases.
  sendCJEOrderEvent({
    orderId: subscription.billing,
    currency: 'USD',
    amount: product.price / 100.0,
    customerStatus:
      user.subscriptions && user.subscriptions.length > 1 ? 'return' : 'new',
  });

  // Send conversion tracking stats to bing
  sendBingEvent(product.id, product.name, product.price / 100.0);
}

export async function authSignUp(authParams) {
  const initParams = await api.users.initializeAuth(
    { username: authParams.username.trim() },
    { fetchPolicy: 'network-only' },
  );
  if (initParams.username !== authParams.username) {
    const err = {
      code: 'UsernameExistsException',
      name: 'UsernameExistsException',
      message: 'An account with the given email already exists.',
    };

    throw err;
  }

  const _userAttributes = keys(authParams.attributes).map((key) => ({
    Name: key,
    Value: authParams.attributes[key],
  }));

  return new Promise((resolve, reject) => {
    reject(err);
  });
}

export async function authSignIn(username, _password) {
  const _initParams = await api.users.initializeAuth(
    { username: username.trim() },
    { fetchPolicy: 'network-only' },
  );

  const authenticationDetails = undefined;

  const user = undefined;
  return new Promise((resolve, reject) => {
    user.authenticateUser(authenticationDetails, {
      onSuccess: (result) => resolve(result),
      onFailure: (err) => reject(err),
    });
  });
}

export async function authRequestPasswordReset(username) {
  if (!username) {
    throw new Error('Empty username');
  }

  const _initParams = await api.users.initializeAuth(
    { username: username.trim() },
    { fetchPolicy: 'network-only' },
  );
  const user = undefined;

  return new Promise((resolve, reject) => {
    user.forgotPassword({
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
      inputVerificationCode: (data) => resolve(data),
    });
  });
}

export async function authConfirmPasswordReset(username, code, newPassword) {
  if (!username) {
    throw new Error('Empty username');
  }

  if (!code) {
    throw new Error('Empty code');
  }

  if (!newPassword) {
    throw new Error('Empty password');
  }

  const _initParams = await api.users.initializeAuth(
    { username: username.trim() },
    { fetchPolicy: 'network-only' },
  );
  const user = undefined;

  return new Promise((resolve, reject) => {
    user.confirmPassword(code, newPassword, {
      onSuccess: resolve,
      onFailure: reject,
    });
  });
}

let _tickedObjects = [];

export function registerTickedObject(obj, interval, callback) {
  unregisterTickedObject(obj);

  const entry = { timer: null, callback, obj };
  _tickedObjects.push(entry);

  const tick = async () => {
    await (entry.callback || obj.tick || (() => {}))();

    if (isActiveTickedObject(obj)) {
      entry.timer = setTimeout(tick, interval);
    }
  };

  tick();
}

export function unregisterTickedObject(obj, finalTick) {
  const entry = _tickedObjects.find((entry) => entry.obj === obj);
  if (entry) {
    _tickedObjects = _tickedObjects.filter((entry) => entry.obj !== obj);

    clearTimeout(entry.timer);
    if (finalTick) {
      (entry.callback || obj.tick || (() => {}))();
    }
  }
}

function isActiveTickedObject(obj) {
  return _tickedObjects.some((entry) => entry.obj === obj);
}

function clearAllTickedObjects() {
  _tickedObjects.forEach((value) => {
    clearTimeout(value.timer);
  });
  _tickedObjects = [];
}

const _state = {
  apiClient: null,
  // Note: authState can be one of 'loading', 'signIn', 'signedIn'
  authState: 'loading',
  authUser: null,
  appConfig: {},
};

export const AppContext = React.createContext({});
/** Hook to access the overall app context */
export function useApp(): ProvidedAppContext {
  return useContext(AppContext);
}

export function withAppContext(WrappedComponent, propName = undefined) {
  return (props) => (
    <AppContext.Consumer>
      {(context) => (
        <WrappedComponent {...props} {...{ [propName || 'app']: context }} />
      )}
    </AppContext.Consumer>
  );
}

/**
 *
 * @returns {ApolloClient}
 */
export function getApiClient() {
  if (!_state.apiClient) {
    throw new Error('Api client is not ready.');
  }

  return _state.apiClient;
}

export function getAuthState() {
  return _state.authState;
}

export function getAppConfig() {
  return _state.appConfig;
}

export function getAppServerUri() {
  return (
    ((window.ReactAppEnv || {}).api || {}).uri ||
    import.meta.env.VITE_API_SERVER_URI
  );
}

function getAuthUser() {
  if (!user) {
    throw new Error('No current user');
  }

  return new Promise((resolve, reject) => {
    user.getSession((err) => {
      if (err) {
        reject(err);
      } else {
        resolve(user);
      }
    });
  });
}

async function getAuthSession() {
  if (!_state.authUser) {
    return null;
  }

  return new Promise((resolve, reject) => {
    _state.authUser.getSession((err, session) => {
      if (err) {
        reject(err);
      } else {
        resolve(session);
      }
    });
  });
}

async function initializeApiClient(getTokenFn) {
  _state.apiClient = await createAuthenticatedApolloClient({
    uri: getAppServerUri(),
    authTokenCallback: getTokenFn,
    cache: {
      freezeResults: true,
      dataIdFromObject: (obj) =>
        obj.__typename && obj.guid
          ? `${obj.__typename}:${obj.guid}`
          : defaultDataIdFromObject(obj),
    },
  });
  _state.appConfig = await getClientAppConfig();
}

export async function initialize(getTokenFn) {
  clearAllTickedObjects();
  Object.assign(_state, {
    apiClient: null,
    authState: 'loading',
    authUser: null,
  });

  await initializeApiClient(getTokenFn);
}

export async function logout() {
  clearAllTickedObjects();

  // Give time to finish any pending requests (e.g. Activity Timer/Log)
  // NOTE: This is a hack and there are a few other options (attach to TickedObjects,
  // mark api calls as pending and wait for resolution, something else?)
  await sleep(100);
  Object.assign(_state, {
    authState: 'signIn',
    authUser: null,
  });
}

export async function login() {
  Object.assign(_state, {
    authState: 'loading',
    authUser: null,
  });

  try {
    const user = undefined;
    if (!user) {
      _state.authState = 'signIn';
      return;
    }

    await new Promise((resolve, reject) => {
      user.getSession((err) => {
        if (err) {
          reject(err);
        } else {
          resolve(user);
        }
      });
    });

    _state.authUser = await getAuthUser();
    _state.authState = 'signedIn';
  } catch (e) {
    console.error(e);
    console.error('User is not authenticated.');
    _state.authState = 'signIn';
  }

  const session = await getAuthSession();
  if (!session) {
    return;
  }

  return await api.users.login();
}

export async function clerkLogin() {
  Object.assign(_state, {
    authState: 'loading',
    authUser: null,
  });

  return await api.users.login();
}

export async function getClientAppConfig(reset) {
  const client = getApiClient();
  const query = gql`
    query GetClientAppConfig {
      clientAppConfig
    }
  `;

  const result = await client.query({
    query,
    ...(reset && { fetchPolicy: 'network-only' }),
  });
  const data = JSON.parse(result.data.clientAppConfig);
  const clientTime = new Date();
  const serverTime = data.serverTime ? new Date(data.serverTime) : clientTime;
  const timeOffset = clientTime - serverTime;

  return {
    ...omit(data, 'serverTime'),
    serverToClientTime: (t) => addMilliseconds(t, timeOffset),
    clientToServerTime: (t) => subMilliseconds(t, timeOffset),
  };
}

export function prepareSearchQueryString(str, defaultKey) {
  const filters = (str || '')
    .split(';')
    .filter((filter) => !isEmpty(filter))
    .map((filter) => {
      const parts = filter.split(/=(.*)/, 2);
      const key = parts.length > 1 ? parts[0].trim() : defaultKey;
      const valueIndex = parts.length > 1 ? 1 : 0;
      const value = escapeSearchQueryString(parts[valueIndex].trim());
      return key ? `${key}=${value}` : value;
    })
    .join(';');
  return filters;
}

export function escapeSearchQueryString(str) {
  str = str.trim();

  const isPattern = str.match(/^@{.*}$/);
  if (isPattern) {
    return str.substring(2, str.length - 1);
  }

  const matchBeginning = str.startsWith('^');
  const matchEnd = str.endsWith('$');
  str = str.slice(matchBeginning ? 1 : 0, matchEnd ? -1 : str.length);
  str = str.replace(/([().%+\-*?[^$])/g, '%$&');
  return `${matchBeginning ? '^' : ''}${str}${matchEnd ? '$' : ''}`;
}

export function calculateEnrollmentId(userId, courseId) {
  return `${userId}_${courseId}`;
}

export function calculateStripePlanId(product) {
  const isAppManaged =
    isEmpty(product.billing) ||
    product.billing === 'auto' ||
    product.billing === calculateStripeProductId(product);

  return isAppManaged ? null : product.billing;
}

export function calculateStripeProductId(product) {
  return product.id && `prod_${product.id}`;
}

export function calculateStripePlanUrl(product, live) {
  const planId = calculateStripePlanId(product);
  return planId
    ? `https://dashboard.stripe.com/${live ? '' : 'test/'}plans/${calculateStripePlanId(product)}`
    : `https://dashboard.stripe.com/${live ? '' : 'test/'}products/${calculateStripeProductId(product)}`;
}

export function priceStringToPrice(price) {
  price = price.trim().replace(/\$/, '').trim();
  return Math.floor(parseFloat(price) * 100);
}

export function priceToPriceString(price) {
  return `$${(price / 100).toFixed(2)}`;
}

export function formatProductPrice(product) {
  const price = priceToPriceString(product.price);
  if (product.autocancel) {
    return price;
  }

  if (product.intervalCount === 1) {
    return `${price} per ${product.interval}`;
  }

  return `${price} billed every ${pluralize(product.interval, product.intervalCount, product.intervalCount > 1)}`;
}

export async function listAllItems(query, params, options) {
  params = { ...params };
  params.first = params.first || 0;
  params.offset = params.offset || 0;
  params.query = params.query && prepareSearchQueryString(params.query);

  options = {
    fetchPolicy: 'no-cache',
    ...options,
  };

  // There seems to be a bug, where no data is return sometimes
  // if cache-first is specified explicitly. So, just allow
  // apollo to default to cache-first if that is what is requested
  if (options.fetchPolicy === 'cache-first') {
    delete options.fetchPolicy;
  }

  const variables = { params: { ...params } };
  const client = getApiClient();
  const result = {
    totalCount: 0,
    items: [],
    cursor: null,
  };

  let moreRequested = true;
  let moreAvailable = true;
  while (moreRequested && moreAvailable) {
    const subResult = await client.query({ query, variables, ...options });
    const data =
      (subResult && subResult.data && values(subResult.data)[0]) || {};
    if (!data.items) {
      console.log(query);
      console.log(params);
      console.log(subResult);
    }
    result.items.push(...(data.items || []));
    result.totalCount = data.totalCount;
    result.cursor = data.cursor;

    variables.params.first = Math.max(0, params.first - result.items.length);
    variables.params.after = data.cursor;

    // If we are running a search, then we will be sending a cursor, so we want to reduce the
    // offset by the amount of items the cursor has already skipped. However, if we are listing
    // items, we should add to the offset, so that the listing will pick up where it left off before
    variables.params.offset = params.query
      ? Math.max(0, params.offset - result.items.length)
      : params.offset + result.items.length;

    moreRequested = !params.first || result.items.length < params.first;
    moreAvailable =
      !!variables.params.after ||
      data.totalCount > result.items.length + params.offset;
  }

  return result;
}

export async function query(query, variables, options) {
  const client = getApiClient();
  const result = await client.query({ query, variables, ...options });
  return result;
}

export async function mutate(mutation, variables, options) {
  const client = getApiClient();
  const result = await client.mutate({ mutation, variables, ...options });
  return result;
}

function extractReferencedFragmentNames(def, results, doc) {
  results = results || [];

  if (def && def.selectionSet && def.selectionSet.selections) {
    def.selectionSet.selections.forEach((child) => {
      if (
        child.kind === 'FragmentSpread' &&
        !results.includes(child.name.value)
      ) {
        results.push(child.name.value);
      }

      extractReferencedFragmentNames(child, results, doc);
    });
  }

  if (def && def.kind === 'FragmentSpread') {
    const fragmentDef = doc.definitions.find((d) => {
      return d.kind === 'FragmentDefinition' && d.name.value === def.name.value;
    });

    if (fragmentDef) {
      extractReferencedFragmentNames(fragmentDef, results, doc);
    }
  }

  return results;
}

export function extractQuery(doc, name) {
  const query = doc.definitions.find(
    (def) => def.kind === 'OperationDefinition' && def.name.value === name,
  );
  if (!query) {
    console.error('Query %s could not be found.', name);
    return null;
  }

  const queryDoc = {
    kind: 'Document',
    definitions: [query],
  };

  const referencedFragments = extractReferencedFragmentNames(query, null, doc);
  referencedFragments.forEach((name) => {
    const fragmentDef = doc.definitions.find((def) => {
      return def.kind === 'FragmentDefinition' && def.name.value === name;
    });

    if (fragmentDef) {
      queryDoc.definitions.push(fragmentDef);
    }
  });

  return queryDoc;
}

function scrubLocalValues(param) {
  if (isArray(param)) {
    return param.map((child) => scrubLocalValues(child));
  }

  if (isPlainObject(param)) {
    return Object.keys(param).reduce((processed, key) => {
      return key.startsWith('_')
        ? processed
        : { ...processed, [key]: scrubLocalValues(param[key]) };
    }, 0);
  }

  return param;
}

export function transformParams(params, transforms) {
  if (!isArray(transforms)) {
    transforms = [transforms];
  }

  transforms.forEach((transform) => {
    if (transform) {
      params = transform(params);
    }
  });

  params = scrubLocalValues(params);

  return params;
}

export function buildQueryMethod(
  doc,
  name,
  paramTransforms,
  prepareForViewing,
  options,
) {
  const queryDoc = extractQuery(doc, name);
  return async (params, queryOptions) => {
    try {
      params = transformParams(params, paramTransforms);
      const result = await query(queryDoc, params, {
        ...options,
        ...queryOptions,
      });
      const entity = values(result.data)[0];
      return prepareForViewing ? prepareForViewing(entity) : entity;
    } catch (e) {
      console.error('Failed to run query %s', name);
      throw e;
    }
  };
}

type ParamTransform = (params: Record<string, any>) => Record<string, any>;

export function buildMutateMethod(
  doc: DocumentNode,
  name: string,
  paramTransforms?: ParamTransform | ParamTransform[],
  prepareForViewing?: ((result: unknown) => unknown) | null,
  options?: MutationOptions,
): (params: Record<any, any>, mutateOptions?: MutationOptions) => Promise<any> {
  const mutation = extractQuery(doc, name);
  return async (params, mutateOptions) => {
    try {
      params = transformParams(params, paramTransforms);
      const result = await mutate(mutation, params, {
        ...options,
        ...mutateOptions,
      });
      const entity = values(result.data)[0];
      return prepareForViewing ? prepareForViewing(entity) : entity;
    } catch (e) {
      console.error('Failed to run mutation %s', name);
      throw e;
    }
  };
}

export function buildListQueryMethod(doc, name) {
  const queryDoc = extractQuery(doc, name);
  return (params, options) => listAllItems(queryDoc, params, options);
}

export const ImageContentFields = `
  id
  name
  alttext
  url
`;

export const VideoContentFields = `
  id
  name
  poster {
    asset {
      url
    }
  }
  sources {
    asset {
      url
      mimetype
    }
  }
  tracks {
    asset {
      url
    }
    label
    kind
    srclang
  }
`;

export const SoundContentFields = `
  id
  name
  sources {
    asset {
      url
    }
  }
`;

export const RichContentFields = `
  text
  images {
    ${ImageContentFields}
  }
  videos {
    ${VideoContentFields}
  }
  sounds {
    ${SoundContentFields}
  }
`;

export const RubricFields = `
  id
  name
  criteria {
    key
    description
    weight
    group
  }
  ratings {
    label
    score
  }
  scoreMethod
  scoreRanges {
    min
    max
    score
  }
`;

const queries = gql`
  query InitializeAuth($username: ID!) {
    initializeAuth(username: $username) {
      username
    }
  }

  fragment ContentGroupFields on ContentGroup {
    id
    name
    courses {
      id
      name
      displayName
    }
  }

  query ContentGroupList($params: ListInput!) {
    contentGroups(params: $params) {
      totalCount
      items {
        ...ContentGroupFields
      }
    }
  }

  fragment RichContentFields on RichContent {
    ${RichContentFields}
  }

  fragment RubricFields on Rubric {
    ${RubricFields}
  }

  fragment CourseDescriptionFields on Course {
    id
    name
    displayName
    upgrade {
      id
      displayName
      migrateProgress
    }
    isPreviewing
    previewConcepts {
      id
    }
  }

  fragment ProgressInfoFields on ProgressInfo {
    viewed
    total
  }

  fragment AssessmentSummaryFields on Assessment {
    id
    cid
    guid
    type
    time
    status
    startedAt
    expiresAt
    updatedAt
    completedAt
    sections {
      id
      name
      displayName
      type
      time
      weight
      startedAt
      expiresAt
      updatedAt
      completedAt
    }
  }

  fragment AssessmentQuestionFields on AssessmentQuestion {
    id
    guid
    response
    score
    content {
      id
      responseType
      multiResponse
      minCorrectAnswers
      reading {
        id
        content { ...RichContentFields }
        numQuestionsPerPage
      }
      readingGroup {
        id
        name
        displayName
        readings {
          displayName
          reading {
            id
            content { ...RichContentFields }
          }
        }
      }
      prompt {
        ...RichContentFields
      }
      answers {
        content {
          ...RichContentFields
        }
        explanation {
          ...RichContentFields
        }
        correct
      }
      explanation {
        ...RichContentFields
      }
      rubric {
        ...RubricFields
      }
    }
    feedback {
      notes {
        id
        content
        source
        deleted
        createdAt
        updatedAt
        updatedBy {
          id
          firstName
          lastName
        }
      }
      rubric {
        ratings {
          criterionKey
          ratingIndex
        }
        source
      }
      score
      source
    }
  }

  fragment AssessmentFields on Assessment {
    ...AssessmentSummaryFields
    questions { ...AssessmentQuestionFields }
    sections {
      questions { ...AssessmentQuestionFields }
    }
  }

  fragment AssessmentStatsFields on AssessmentStats {
    id
    cid
    guid
    time
    type
    questions { ...ProgressInfoFields }
    sections {
      id
      name
      displayName
      time
      defaultTime
      weight
      questions { ...ProgressInfoFields }
      crqScoreSources
      score {
        raw
        max
        weight
        normalized
      }
      crqScore {
        raw
        max
        normalized
      }
      mcqScore {
        raw
        max
        normalized
      }
    }
    score {
      raw
      max
      normalized
    }
    mcqScore {
      normalized
      raw
      max
    }
    crqScore {
      raw
      max
      normalized
    }
    crqScoreSources
    status
    startedAt
    expiresAt
    completedAt
  }

  fragment ConceptStatsFields on ConceptStats {
    id
    guid
    materials { ...ProgressInfoFields }
    cards { ...ProgressInfoFields }
    questions { ...ProgressInfoFields }
    quizzes { ...ProgressInfoFields }
    score {
      raw
      max
    }
    progress
    hide
  }

  fragment EnrollmentStatsFields on EnrollmentStats {
    questions { ...ProgressInfoFields }
    materials { ...ProgressInfoFields }
    cards { ...ProgressInfoFields }
    quizzes: assessments(type: "quiz") {
      id
      guid
      score {
        normalized
      }
    }
    concepts { ...ConceptStatsFields }
    assessments { ...AssessmentStatsFields }
    practiceTests: assessments(type: "practice") { ...AssessmentStatsFields }
    diagnosticTests: assessments(type: "diagnostic") { ...AssessmentStatsFields }
    progress
  }

  fragment EnrollmentSummaryFields on Enrollment {
    id
    createdAt
    updatedAt
    course {
      ...CourseDescriptionFields
      assessments {
        cid
        type
        time
        questions
      }
    }
    stats { ...EnrollmentStatsFields }
    activity { ...EnrollmentActivityFields }
    user { id }
    access
    product { ...ProductFields }
  }

  fragment EnrollmentStatsSummaryUpdateFields on Enrollment {
    id
    updatedAt
    stats {
      questions { ...ProgressInfoFields }
      materials { ...ProgressInfoFields }
      cards { ...ProgressInfoFields }
      progress
    }
    access
  }

  fragment EnrollmentActivityFields on EnrollmentActivity {
    quizzes
    materials
    cards
  }

  mutation EnrollmentViewDashboard($id: ID!) {
    enrollmentAccess(id: $id) {
      ...EnrollmentSummaryFields
      studyPlan {
        unlocked
      }
      course {
        id
        sections {
          id
          name
          concepts {
            id
            name
            examNodes {
              index
            }
          }
        }
        examNodes {
          index
          parent
          key
          name
          description
        }
        upgrade {
          id
          displayName
          migrateProgress
        }
        introVideo {
          ${VideoContentFields}
        }
        previewConcepts {
          id
        }
      }
    }
  }

  mutation EnrollmentClaim($id: ID!) {
    enrollmentClaim(id: $id) {
      ...EnrollmentSummaryFields
    }
  }

  mutation EnrollmentAddPracticeTestAttempt($id: ID!) {
    enrollmentAddPracticeTestAttempt(id: $id) {
      ...EnrollmentSummaryFields
    }
  }

  query EnrollmentExport($id: ID!, $compressed: Boolean) {
    enrollmentExport(id: $id, compressed: $compressed)
  }

  mutation EnrollmentImport($id: ID!, $data: String!, $allowCourseMismatch: Boolean) {
    enrollmentImport(id: $id, data: $data, allowCourseMismatch: $allowCourseMismatch) {
      ...EnrollmentSummaryFields
      stats {
        quizzes: assessments(type: "quiz") { ...AssessmentStatsFields }
      }
    }
  }

  query EnrollmentGet($id: ID!, $context: ClientContext) {
    enrollment(id: $id, context: $context) {
      ...EnrollmentSummaryFields
    }
  }

  query EnrollmentList($params: ListInput!) {
    enrollments(params: $params) {
      totalCount
      cursor
      items {
        ...EnrollmentSummaryFields
      }
    }
  }

  mutation EnrollmentCreate($user: ID!, $course: ID!, $product: ID) {
    enrollmentCreate(user: $user, course: $course, product: $product) {
      ...EnrollmentSummaryFields
      stats {
        quizzes: assessments(type: "quiz") { ...AssessmentStatsFields }
      }
      user {
        enrollments { ...EnrollmentSummaryFields }
      }
    }
  }

  mutation EnrollmentDelete($id: ID!) {
    enrollmentDelete(id: $id) { id }
  }

  mutation EnrollmentConceptStatsRecalculate($id: ID!) {
    enrollmentConceptStatsRecalculate(id: $id) { id }
  }

  mutation EnrollmentViewContent($id: ID!, $content: [ID]) {
    viewContent(enrollment: $id, content: $content) {
      logs {
        id
        entries {
          id
        }
      }
      stats { ...ConceptStatsFields }
      enrollment { ...EnrollmentStatsSummaryUpdateFields }
    }
  }

  mutation EnrollmentLogActivity($id: ID!, $activity: String!, $time: Int!) {
    enrollmentLogActivity(id: $id, activity: $activity, time: $time) {
      id
      activity {
        quizzes
        materials
        cards
        total
      }
      updatedAt
      accessedAt
    }
  }

  mutation EnrollmentStartAssessment($id: ID!, $assessment: ID!, $reset: Boolean, $time: DateTime, $context: ClientContext) {
    assessmentStart(enrollment: $id, assessment: $assessment, reset: $reset, time: $time, context: $context) {
      ...AssessmentFields
    }
  }

  mutation EnrollmentResetAssessment($id: ID!, $assessment: ID!, $context: ClientContext) {
    assessmentReset(enrollment: $id, assessment: $assessment, context: $context) {
      ...AssessmentFields
    }
  }

  mutation EnrollmentSubmitAssessment($id: ID!, $assessment: ID!, $responses: [String], $context: ClientContext) {
    assessmentComplete(enrollment: $id, assessment: $assessment, responses: $responses, context: $context) {
      assessment {
        ...AssessmentFields
      }
      astat { ...AssessmentStatsFields }
      cstat { ...ConceptStatsFields }
      enrollment { ...EnrollmentStatsSummaryUpdateFields }
    }
  }

  mutation EnrollmentForceAssessmentStatus($id: ID!, $assessment: ID!, $forceStatus: String, $context: ClientContext) {
    assessmentForceStatus(enrollment: $id, assessment: $assessment, status: $forceStatus, context: $context) {
      ...AssessmentStatsFields
    }
  }

  mutation EnrollmentForceAssessmentTime($id: ID!, $params: [AssessmentTimeInput]!) {
    assessmentForceTime(enrollment: $id, params: $params) {
      ...AssessmentStatsFields
    }
  }

  mutation EndrollmentAssessmentSubmitFeedback($enrollment: ID!, $assessment: ID!, $question: ID!, $feedback: JSON!, $context: ClientContext){
    assessmentSubmitFeedback(enrollment: $enrollment, assessment: $assessment, question: $question, feedback: $feedback, context: $context) {
        assessment { ...AssessmentFields }
        question { ...AssessmentQuestionFields }
        section { ...AssessmentFields }
        enrollment {
          ...EnrollmentStatsSummaryUpdateFields
          stats {
            practiceTests: assessments(type: "practice") { ...AssessmentStatsFields }
          }
        }
    }
  }

  mutation EnrollmentAssessmentSubmitFeedbackSource($enrollment: ID!, $assessment: ID!, $question: ID!, $source: String!, $context: ClientContext){
    assessmentSubmitFeedbackSource(enrollment: $enrollment, assessment: $assessment, question: $question, source: $source, context: $context) {
        assessment { ...AssessmentFields }
        question { ...AssessmentQuestionFields }
        section { ...AssessmentFields }
        enrollment { ...EnrollmentStatsSummaryUpdateFields }
    }
  }

  mutation EnrollmentSubmitAssessmentResponse($id: ID!, $assessment: ID!, $question: ID!, $response: String!, $index: Int, $context: ClientContext) {
    assessmentSubmitResponse(enrollment: $id, assessment: $assessment, question: $question, response: $response, index: $index, context: $context) {
      question { ...AssessmentQuestionFields }
      astat { ...AssessmentStatsFields }
      cstat { ...ConceptStatsFields }
      enrollment { ...EnrollmentStatsSummaryUpdateFields }
    }
  }

  mutation GradingRequestCreate($user: ID!, $enrollment: ID!, $assessment:  ID!, $question: ID!, $response: String!) {
    gradingRequestCreate(user: $user, enrollment: $enrollment, assessment: $assessment, question: $question, response: $response) {
      id
      }
    }

  fragment UserSummaryFields on User {
    id
    firstName
    lastName
    email
    createdAt
    updatedAt
    loggedInAt
    groups
    live
    credits {
      grading
    }
    tfCompanyData {
      desiredGradeLevels
      desiredStates
      certificationStatusCurrent
    }
    job {
      city
      state
      description
      commuteDistance
      subjects
      grades
      isCertified
      isInCertificationProgram
      hasPassedCertificationExam
      isCertificationOther
      certificationStatusOther
      testsStudying
      testsPassed
      gradeLevels
      roles
      states
      cities
      certificationStatusCurrent
      certificationStates
      certificationTestsPassed
      workingStart
      workingStartOther
      educationHighest
      educationMajorField
      educationSchoolName
      educationGraduationDate
      workExperienceJobTitle
      workExperienceJobEmployer
      workExperienceJobStartDate
      workExperienceJobEndDate
      workExperienceWorkHere
      phoneNumber
      jobSearchLookingForJob
      jobSearchNotLookingWhy
      jobSearchNotLookingWhyOther
      jobSearchGetStarted
      jobSearchCreateProfile
      jobSearchPrimaryState
      agreedToShareInformation
      doNotAskAgain
      searchRecommended
      searchSaved
      searchSavedEntries
      jobUpdatedAt
    }
  }

  fragment BillingInfoFields on BillingInfo {
    method
    account
    expMonth,
    expYear,
  }

  fragment InvoiceFields on Invoice {
    description
    createdAt
    periodStartedAt
    periodExpiredAt
    amount
    status
    url
  }

  fragment SubscriptionFields on Subscription {
    billing
    createdAt
    expiresAt
    status
    product { ...ProductFields }
    key {
      id
      claimedAt
      expiresAt
      duration
    }
  }

  query ListProviderUsers($params: ListInput) {
    providerUsers(params: $params) {
      cursor
      items {
        uuid
        email
        firstName
        lastName
        user { ...UserSummaryFields }
      }
    }
  }

  query UserGet($id: ID!, $context: ClientContext) {
    user(id: $id, context: $context) {
      ...UserSummaryFields
    }
  }

  query UserList($params: ListInput) {
    users(params: $params) {
      totalCount
      cursor
      items {
        ...UserSummaryFields
      }
    }
  }

  query UserGetBilling($id: ID!) {
    user(id: $id) {
      id
      billing { ...BillingInfoFields }
    }
  }

  query UserGetSubscriptions($id: ID!) {
    user(id: $id) {
      id
      subscriptions { ...SubscriptionFields }
    }
  }

  query UserGetInvoices($id: ID!) {
    user(id: $id) {
      id
      invoices { ...InvoiceFields }
    }
  }

  query UserGetEnrollments($id: ID!, $context: ClientContext) {
    user(id: $id, context: $context) {
      id
      enrollments { ...EnrollmentSummaryFields }
    }
  }

  query UserGetMemberships($id: ID!) {
    user(id: $id) {
      id
      memberships { ...UserMembershipSummaryFields }
    }
  }

  query UserGetJobSearchSaved($id: ID!) {
    jobSearchSaved(id: $id) {
      savedJobs
    }
  }

  query UserGetCourses($id: ID!) {
    user(id: $id) {
      id
      courses { ...CourseDescriptionFields }
    }
  }

  mutation ClerkOnboarding($id: ID!) {
    clerkOnboarding(id: $id) {
      success
    }
  }

  mutation UserLogin {
    login {
      ...UserSummaryFields
      memberships {
        ...UserMembershipSummaryFields
      }
      subscriptions {
        ...SubscriptionFields
      }
    }
  }

  mutation CreatePreviewSubscriptionInvoice($user: ID!, $products: [ID], $payment: String!, $coupon: String) {
    previewSubscriptionInvoiceCreate(user: $user, products: $products, payment: $payment, coupon: $coupon) {
      discount
      subtotal
      tax
      total
    }
  }

  mutation UserCancelSubscription($user: ID!, $subscription: ID!, $timing: String) {
    subscriptionCancel(user: $user, subscription: $subscription, timing: $timing) {
      ...UserSummaryFields
      subscriptions { ...SubscriptionFields }
    }
  }

  mutation UserCreateSubscription($user: ID!, $product: ID!, $payment: String, $coupon: String) {
    subscriptionCreate(user: $user, product: $product, payment: $payment, coupon: $coupon) {
      ...UserSummaryFields
      subscriptions { ...SubscriptionFields }
    }
  }

  mutation UserRequestEmailVerification($email: String!, $reason: String) {
    userRequestEmailVerification(email: $email, reason: $reason)
  }

  mutation UserConfirmEmailVerification($code: String!) {
    userConfirmEmailVerification(code: $code)
  }

  mutation UserUpdateBilling($id: ID!, $payment: String!) {
    userUpdateBilling(id: $id, payment: $payment) {
      ...UserSummaryFields
      billing { ...BillingInfoFields }
    }
  }

  mutation UserUpdate($id: ID!, $params: UserInput!) {
    userUpdate(id: $id, params: $params) {
      ...UserSummaryFields,
    }
  }

  mutation UserDelete($id: ID!) {
    userDelete(id: $id) { id }
  }

  mutation UserSubmitNPSSurvey($params: NPSSurveyInput!) {
    userSubmitNPSSurvey(params: $params) {
      id
      user
      course
      product
      event
      date
      nps
      comment
    }
  }

  fragment OrganizationTreeFields on Organization {
    parent {
      id
      name
    }
    children {
      id
      name
    }
  }

  fragment OrganizationSummaryFields on Organization {
    id
    name
    slug
    crqGradingStatus
  }

  fragment OrganizationAdminFields on Organization {
    createdAt
    updatedAt
    status
    enabledProducts
  }

  fragment ActionFields on Action {
    type
    name
    params
  }

  query OrganizationList($params: ListInput) {
    organizations(params: $params) {
      totalCount
      cursor
      items {
        ...OrganizationSummaryFields
        ...OrganizationAdminFields
        ...OrganizationTreeFields
      }
    }
  }

  query OrganizationGet($id: ID!) {
    organization(id: $id) {
      ...OrganizationSummaryFields
    }
  }

  query OrganizationGetAdmin($id: ID!) {
    organization(id: $id) {
      ...OrganizationSummaryFields
      ...OrganizationAdminFields
      ...OrganizationTreeFields
      memberships {
        totalCount
        items {
          ...MembershipSummaryFields
        }
      }
      actions { ...ActionFields }
    }
  }

  mutation OrganizationCreate($params: OrganizationInput!) {
    organizationCreate(params: $params) {
      ...OrganizationSummaryFields
      ...OrganizationAdminFields
      ...OrganizationTreeFields
      actions { ...ActionFields }
    }
  }

  mutation OrganizationUpdate($id: ID!, $params: OrganizationInput!) {
    organizationUpdate(id: $id, params: $params) {
      ...OrganizationSummaryFields
      ...OrganizationAdminFields
      ...OrganizationTreeFields
      actions { ...ActionFields }
    }
  }

  mutation OrganizationDelete($id: ID!) {
    organizationDelete(id: $id) { id }
  }

  fragment UserMembershipSummaryFields on Membership {
    id
    createdAt
    updatedAt
    org {
      ...OrganizationSummaryFields
      parent {
        id
      }
    }
    role
  }

  fragment MembershipSummaryFields on Membership {
    ...UserMembershipSummaryFields
    user { ...UserSummaryFields }
  }

  mutation MembershipCreate($params: MembershipInput!) {
    membershipCreate(params: $params) {
      ...MembershipSummaryFields
    }
  }

  mutation MembershipDelete($id: ID!) {
    membershipDelete(id: $id) { id }
  }

  fragment ProductFields on Product {
    id
    name
    description
    org {
      id
      name
    }
    slug
    scope {
      groups {
        ...ContentGroupFields
      }
    }
    consumables {
      type
      key
      quantity
    }
    price
    createdAt
    updatedAt
    live
    billing
    statementDescriptor
    autocancel
    interval
    intervalCount
  }

  query ProductGet($id: ID!) {
    product(id: $id) {
      ...ProductFields
    }
  }

  query ProductGetPreview($id: ID!, $courses: [ID]) {
    product(id: $id) {
      ...ProductFields
      courses(ids: $courses) {
        id
        name
        displayName
        sections {
          id
          name
          concepts {
            id
            name
          }
        }
        assessments {
          cid
          type
          time
          questions
        }
      }
    }
  }

  query ProductList($params: ListInput) {
    products(params: $params) {
      totalCount
      cursor
      items {
        ...ProductFields
      }
    }
  }

  mutation ProductCreate($params: ProductInput!) {
    productCreate(params: $params) {
      ...ProductFields
    }
  }

  mutation ProductUpdate($id: ID!, $params: ProductInput!) {
    productUpdate(id: $id, params: $params) {
      ...ProductFields
    }
  }

  mutation ProductDelete($id: ID!) {
    productDelete(id: $id) { id }
  }

  fragment ProductKeyFields on ProductKey {
    id
    products {
      id
      name
    }
    courses { ...CourseDescriptionFields }
    createdAt
    claimedAt
    expiresAt
    claimedBy {
      id
      email
    }
    org {
      id
      name
    }
    duration
    status
    prefix
    multiUse
    payment
  }

  query ProductKeyGet($id: ID!) {
    productKey(id: $id) {
      ...ProductKeyFields
    }
  }

  query ProductKeyList($params: ListInput) {
    productKeys(params: $params) {
      totalCount
      cursor
      items {
        ...ProductKeyFields
      }
    }
  }

  mutation ProductKeyCreate($params: ProductKeyCreateInput!) {
    productKeyCreate(params: $params) {
      ...ProductKeyFields
    }
  }

  mutation ProductKeyUpdate($id: ID!, $params: ProductKeyUpdateInput!) {
    productKeyUpdate(id: $id, params: $params) {
      ...ProductKeyFields
    }
  }

  mutation ProductKeyDelete($id: ID!) {
    productKeyDelete(id: $id) { id }
  }

  query CourseGet($id: ID!) {
    course(id: $id) { ...CourseDescriptionFields }
  }

  fragment InviteFields on Invite {
    id
    createdAt
    updatedAt
    expiresAt
    claimedAt
    email
    sent
    status
    firstName
    lastName
    payment
    template
    unsubscribe
    billing
    billingOrg { ...OrganizationSummaryFields }
    duration
    user { ...UserSummaryFields }
    org { ...OrganizationSummaryFields }
    products { ...ProductFields }
    courses { ...CourseDescriptionFields }
    key { ...ProductKeyFields }
    role
    url
  }

  query InviteGet($id: ID!) {
    invite(id: $id) { ...InviteFields }
  }

  query InviteList($params: ListInput) {
    invites(params: $params) {
      totalCount
      cursor
      items {
        ...InviteFields
      }
    }
  }

  mutation InviteCreate($params: InviteInput!) {
    inviteCreate(params: $params) { ...InviteFields }
  }

  mutation InviteUpdate($id: ID!, $params: InviteInput!) {
    inviteUpdate(id: $id, params: $params) {
      ...InviteFields
    }
  }

  mutation InviteResend($id: ID!, $params: InviteInput) {
    inviteResend(id: $id, params: $params) {
      ...InviteFields
    }
  }

  mutation InviteAccept($token: String!, $payment: String, $confirm: Boolean) {
    inviteAccept(token: $token, payment: $payment, confirm: $confirm) {
      id
      email
      firstName
      lastName
      payment
      products {
        id
        name
        description
        org {
          id
        }
        slug
        scope {
          groups {
            ...ContentGroupFields
          }
        }
        consumables {
          type
          key
          quantity
        }
        price
        createdAt
        updatedAt
        live
        billing
        statementDescriptor
        autocancel
        interval
        intervalCount
      }
      status
      user {
        ...UserSummaryFields
        memberships { ...UserMembershipSummaryFields }
        courses { ...CourseDescriptionFields }
      }
      org {
        id
        name
      }
    }
  }

  mutation InviteDelete($id: ID!) {
    inviteDelete(id: $id) { id }
  }
`;

function combineIdParams(...fields) {
  return (params) => {
    const id = fields.map((field) => params[field]).join('_');
    params = omit(params, fields);
    return { id, ...params };
  };
}

function updateParams(...inputParams) {
  return (entity) => {
    const id = entity.id;
    const params = {};

    inputParams.forEach((key) => {
      const isRef = key.startsWith('@');
      const path = isRef ? key.substring(1) : key;
      let val = get(entity, path);
      if (isRef) {
        val = isArray(val)
          ? val.map((v) => (isString(v) ? v : v && v.id))
          : isString(val)
            ? val
            : val && val.id;

        if (val === undefined) {
          return;
        }
      }
      set(params, path, val);
    });
    return { id, params };
  };
}

function completeEnrollmentDelete() {
  // We don't delete enrollments very often and it is difficult to know
  // what could be cached related to an enrollment, so just reset the whole cache
  getApiClient().resetStore();
}

export function cacheUserEnrollment(store, enrollment) {
  const userId = enrollment && enrollment.user && enrollment.user.id;
  if (!userId || !enrollment.user) {
    return;
  }

  const id = `User:${userId}`;
  const fragment = gql`
    fragment CreateEnrollmentFields on User {
      enrollments {
        id
      }
    }
  `;

  try {
    const oldData = store.readFragment({ fragment, id });
    const newData = {
      ...oldData,
      enrollments: oldData.enrollments
        .filter((item) => item.id !== enrollment.id)
        .concat(enrollment),
    };
    store.writeFragment({ fragment, id, data: newData });
  } catch (e) {
    console.error(e);
    // Ignore errors. It probably just means the data wasn't fetched before
  }
}

function completeEnrollmentCreate(store, { data }) {
  const enrollment = values(data)[0];
  cacheUserEnrollment(store, enrollment);
}

export const api = {
  enrollments: {
    list: buildListQueryMethod(queries, 'EnrollmentList'),
    get: buildQueryMethod(queries, 'EnrollmentGet'),
    import: buildMutateMethod(queries, 'EnrollmentImport'),
    export: buildQueryMethod(queries, 'EnrollmentExport', null, null, {
      fetchPolicy: 'network-only',
    }),
    viewDashboard: buildMutateMethod(queries, 'EnrollmentViewDashboard'),
    viewContent: buildMutateMethod(queries, 'EnrollmentViewContent'),
    logActivity: buildMutateMethod(queries, 'EnrollmentLogActivity'),
    claim: buildMutateMethod(queries, 'EnrollmentClaim', null, null, {
      update: completeEnrollmentCreate,
    }),
    addPracticeTestAttempt: buildMutateMethod(
      queries,
      'EnrollmentAddPracticeTestAttempt',
    ),
    create: buildMutateMethod(queries, 'EnrollmentCreate', null, null, {
      update: completeEnrollmentCreate,
    }),
    delete: buildMutateMethod(queries, 'EnrollmentDelete', null, null, {
      update: completeEnrollmentDelete,
    }),
    recalculateConceptProgress: buildMutateMethod(
      queries,
      'EnrollmentConceptStatsRecalculate',
    ),
    startAssessment: buildMutateMethod(queries, 'EnrollmentStartAssessment'),
    resetAssessment: buildMutateMethod(queries, 'EnrollmentResetAssessment'),
    submitAssessmentFeedback: buildMutateMethod(
      queries,
      'EndrollmentAssessmentSubmitFeedback',
      null,
      (result) => result.question,
    ),
    submitAssessmentFeedbackSource: buildMutateMethod(
      queries,
      'EnrollmentAssessmentSubmitFeedbackSource',
      null,
      (result) => result.question,
    ),
    submitAssessmentResponse: buildMutateMethod(
      queries,
      'EnrollmentSubmitAssessmentResponse',
      null,
      (result) => result.question,
    ),
    submitAssessment: buildMutateMethod(
      queries,
      'EnrollmentSubmitAssessment',
      null,
      (result) => result.assessment,
    ),
    forceAssessmentStatus: buildMutateMethod(
      queries,
      'EnrollmentForceAssessmentStatus',
    ),
    forceAssessmentTime: buildMutateMethod(
      queries,
      'EnrollmentForceAssessmentTime',
    ),
    createGradingRequest: buildMutateMethod(queries, 'GradingRequestCreate'),
  },
  users: {
    initializeAuth: buildQueryMethod(queries, 'InitializeAuth'),
    list: buildListQueryMethod(queries, 'UserList'),
    listAWSUsers: buildListQueryMethod(queries, 'ListProviderUsers'),
    get: buildQueryMethod(queries, 'UserGet'),
    getBilling: buildQueryMethod(queries, 'UserGetBilling'),
    getInvoices: buildQueryMethod(queries, 'UserGetInvoices'),
    getSubscriptions: buildQueryMethod(queries, 'UserGetSubscriptions'),
    getCourses: buildQueryMethod(queries, 'UserGetCourses'),
    getEnrollments: buildQueryMethod(queries, 'UserGetEnrollments'),
    getMemberships: buildQueryMethod(queries, 'UserGetMemberships'),
    getJobSearchSaved: buildQueryMethod(queries, 'UserGetJobSearchSaved'),
    login: buildMutateMethod(queries, 'UserLogin'),
    clerkOnboarding: buildMutateMethod(queries, 'ClerkOnboarding'),
    update: buildMutateMethod(
      queries,
      'UserUpdate',
      updateParams('lastName', 'firstName', 'email', 'tfCompanyData'),
    ),
    updateBilling: buildMutateMethod(queries, 'UserUpdateBilling'),
    createPreviewSubscriptionInvoice: buildMutateMethod(
      queries,
      'CreatePreviewSubscriptionInvoice',
    ),
    cancelSubscription: buildMutateMethod(queries, 'UserCancelSubscription'),
    createSubscription: buildMutateMethod(queries, 'UserCreateSubscription'),
    requestEmailVerification: buildMutateMethod(
      queries,
      'UserRequestEmailVerification',
    ),
    confirmEmailVerification: buildMutateMethod(
      queries,
      'UserConfirmEmailVerification',
    ),
    delete: buildMutateMethod(queries, 'UserDelete'),
    submitNPSSurvey: buildMutateMethod(queries, 'UserSubmitNPSSurvey'),
  },
  organizations: {
    list: buildListQueryMethod(queries, 'OrganizationList'),
    get: buildQueryMethod(queries, 'OrganizationGet'),
    getAdmin: buildQueryMethod(
      queries,
      'OrganizationGetAdmin',
      null,
      (org) =>
        org && {
          ...org,
          memberships: (org && org.memberships.items) || [],
        },
    ),
    create: buildMutateMethod(
      queries,
      'OrganizationCreate',
      updateParams(
        'name',
        'slug',
        'status',
        'actions',
        'crqGradingStatus',
        'enabledProducts',
        '@parent',
      ),
    ),
    update: buildMutateMethod(
      queries,
      'OrganizationUpdate',
      updateParams(
        'name',
        'slug',
        'status',
        'actions',
        'crqGradingStatus',
        'enabledProducts',
        '@parent',
      ),
    ),
    delete: buildMutateMethod(queries, 'OrganizationDelete'),
  },
  products: {
    get: buildQueryMethod(queries, 'ProductGet'),
    getPreview: buildQueryMethod(queries, 'ProductGetPreview'),
    list: buildListQueryMethod(queries, 'ProductList'),
    create: buildMutateMethod(
      queries,
      'ProductCreate',
      updateParams(
        'name',
        'description',
        '@org',
        'slug',
        '@scope.groups',
        'price',
        'live',
        'billing',
        'statementDescriptor',
        'interval',
        'intervalCount',
        'autocancel',
        'consumables',
      ),
    ),
    update: buildMutateMethod(
      queries,
      'ProductUpdate',
      updateParams(
        'name',
        'description',
        '@org',
        'slug',
        '@scope.groups',
        'price',
        'live',
        'billing',
        'statementDescriptor',
        'interval',
        'intervalCount',
        'autocancel',
        'consumables',
      ),
    ),
    delete: buildMutateMethod(queries, 'ProductDelete'),
  },
  productKeys: {
    get: buildQueryMethod(queries, 'ProductKeyGet'),
    list: buildListQueryMethod(queries, 'ProductKeyList'),
    create: buildMutateMethod(
      queries,
      'ProductKeyCreate',
      updateParams(
        'prefix',
        'count',
        '@products',
        '@courses',
        '@org',
        'expiresAt',
        'duration',
        'expiresAt',
        'status',
        'multiUse',
      ),
    ),
    update: buildMutateMethod(
      queries,
      'ProductKeyUpdate',
      updateParams('status'),
    ),
    delete: buildMutateMethod(queries, 'ProductKeyDelete'),
  },
  content: {
    groups: {
      list: buildListQueryMethod(queries, 'ContentGroupList'),
    },
    courses: {
      get: buildQueryMethod(queries, 'CourseGet'),
    },
  },
  memberships: {
    create: buildMutateMethod(
      queries,
      'MembershipCreate',
      updateParams('@org', '@user', 'role'),
    ),
    delete: buildMutateMethod(
      queries,
      'MembershipDelete',
      combineIdParams('org', 'user'),
    ),
  },
  invites: {
    list: buildListQueryMethod(queries, 'InviteList'),
    get: buildQueryMethod(queries, 'InviteGet'),
    create: buildMutateMethod(
      queries,
      'InviteCreate',
      updateParams(
        'email',
        '@org',
        '@billingOrg',
        '@products',
        '@courses',
        'duration',
        'expiresAt',
        'prefix',
        'send',
        'payment',
        'template',
        'unsubscribe',
        'firstName',
        'lastName',
        'role',
      ),
    ),
    update: buildMutateMethod(
      queries,
      'InviteUpdate',
      updateParams(
        'email',
        'send',
        'payment',
        'template',
        'unsubscribe',
        'firstName',
        'lastName',
        'role',
      ),
    ),
    accept: buildMutateMethod(queries, 'InviteAccept'),
    delete: buildMutateMethod(queries, 'InviteDelete'),
    resend: buildMutateMethod(queries, 'InviteResend'),
  },
};

api.Product = api.products;
api.User = api.users;
api.Enrollment = api.enrollments;
api.Organization = api.organizations;
api.ProductKey = api.productKeys;
api.Membership = api.memberships;
api.Invite = api.invites;

export function isPassingQuizScore(score) {
  // eslint-disable-next-line prefer-const
  let { normalized, raw, max } = score || {};

  if (!isNumber(normalized) && max > 0) {
    normalized = raw / max;
  }

  return normalized && Math.round(normalized * 100) >= 70;
}
